import React, { useState, useEffect } from 'react';
import { promptTemplateService, PromptTemplate } from '../services/promptTemplateService';

interface GlobalTemplateManagerProps {
  isVisible: boolean;
  onClose: () => void;
}

interface TemplateFormData {
  title: string;
  prompt: string;
  description: string;
  category: string;
}

const GlobalTemplateManager: React.FC<GlobalTemplateManagerProps> = ({ isVisible, onClose }) => {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  
  const categories = ['general', 'analysis', 'report', 'legal', 'underwriting', 'workflow'];
  
  // Form state
  const [formData, setFormData] = useState<TemplateFormData>({
    title: '',
    prompt: '',
    description: '',
    category: 'general'
  });

  // Statistics
  const [stats, setStats] = useState<{
    totalTemplates: number;
    totalUsage: number;
    topTemplates: Array<{ id: string; title: string; usageCount: number; isGlobal: boolean }>;
  } | null>(null);

  useEffect(() => {
    if (isVisible) {
      loadTemplates();
      loadStats();
    }
  }, [isVisible, showInactive]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await promptTemplateService.getGlobalTemplates({
        isActive: !showInactive,
        sortBy: 'createdAt',
        sortOrder: 'DESC'
      });
      setTemplates(response.templates);
    } catch (err) {
      setError('Failed to load global templates');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const stats = await promptTemplateService.getGlobalTemplateStats();
      setStats(stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      setError(null);
      await promptTemplateService.createGlobalTemplate(formData);
      await loadTemplates();
      await loadStats();
      setIsCreating(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to create global template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      setError(null);
      await promptTemplateService.updateGlobalTemplate(selectedTemplate.id, formData);
      await loadTemplates();
      setIsEditing(false);
      setSelectedTemplate(null);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to update global template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this global template? This will affect all users.')) return;
    
    try {
      setError(null);
      await promptTemplateService.deleteGlobalTemplate(templateId);
      await loadTemplates();
      await loadStats();
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete global template');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      prompt: '',
      description: '',
      category: 'general'
    });
  };

  const startEdit = (template: PromptTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      prompt: template.prompt || '',
      description: template.description || '',
      category: template.category
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setSelectedTemplate(null);
    resetForm();
  };

  const filteredTemplates = templates.filter(template => {
    if (searchQuery && !template.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !template.description?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedCategory && template.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  const extractVariables = (prompt: string): string[] => {
    return promptTemplateService.extractVariables(prompt);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Global Prompt Templates</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col p-6">
          {/* Statistics */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Total Global Templates</h3>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTemplates}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Total Usage (All Users)</h3>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalUsage}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Most Used Global Template</h3>
                <p className="text-lg font-semibold truncate text-gray-900 dark:text-white">
                  {stats.topTemplates[0]?.title || 'N/A'}
                </p>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Show Inactive</span>
              </label>
              <button
                onClick={startCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                New Global Template
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Template List */}
            <div className="lg:col-span-1 overflow-y-auto">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Global Templates ({filteredTemplates.length})</h3>
                </div>
                
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-500 dark:text-gray-400">Loading templates...</p>
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <p>No global templates found</p>
                    <button
                      onClick={startCreate}
                      className="mt-4 text-blue-600 hover:text-blue-700"
                    >
                      Create first global template
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-600">
                    {filteredTemplates.map(template => (
                      <div
                        key={template.id}
                        className={`p-4 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors ${
                          selectedTemplate?.id === template.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                        onClick={() => setSelectedTemplate(template)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{template.title}</h4>
                            {template.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {template.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                              <span className="bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white px-2 py-1 rounded">
                                {template.category}
                              </span>
                              <span>{template.usageCount} uses</span>
                              <span className="text-blue-600 dark:text-blue-400">Global</span>
                              {!template.isActive && (
                                <span className="text-red-600 dark:text-red-400">Inactive</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEdit(template);
                              }}
                              className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Edit template"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTemplate(template.id);
                              }}
                              className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                              title="Delete template"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Template Details/Editor */}
            <div className="lg:col-span-1 overflow-y-auto">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isCreating ? 'Create Global Template' : isEditing ? 'Edit Global Template' : 'Template Details'}
                  </h3>
                </div>
                
                {(isCreating || isEditing) ? (
                  <div className="p-4">
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      isCreating ? handleCreateTemplate() : handleUpdateTemplate();
                    }}>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Title</label>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                            required
                            maxLength={100}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Category</label>
                          <select
                            value={formData.category}
                            onChange={(e) => setFormData({...formData, category: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                          >
                            {categories.map(cat => (
                              <option key={cat} value={cat}>
                                {cat.charAt(0).toUpperCase() + cat.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Description (optional)</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                            rows={2}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                            Prompt
                            {formData.prompt && extractVariables(formData.prompt).length > 0 && (
                              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                Variables: {extractVariables(formData.prompt).map(v => `{${v}}`).join(', ')}
                              </span>
                            )}
                          </label>
                          <textarea
                            value={formData.prompt}
                            onChange={(e) => setFormData({...formData, prompt: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono text-sm"
                            rows={20}
                            required
                            maxLength={10000}
                            placeholder="Enter your global prompt template here. Use {variable_name} for variables."
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formData.prompt.length}/10,000 characters
                          </p>
                        </div>
                        
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            <strong>Note:</strong> Global templates are visible to all users but they can only see the title. The prompt content remains hidden.
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            {isCreating ? 'Create' : 'Update'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                ) : selectedTemplate ? (
                  <div className="p-4">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-lg text-gray-900 dark:text-white">{selectedTemplate.title}</h4>
                        {selectedTemplate.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {selectedTemplate.description}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white px-3 py-1 rounded-full text-sm">
                          {selectedTemplate.category}
                        </span>
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm">
                          {selectedTemplate.usageCount} uses across all users
                        </span>
                        <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-sm">
                          Global Template
                        </span>
                        {!selectedTemplate.isActive && (
                          <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-full text-sm">
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      <div>
                        <h5 className="font-medium mb-2 text-gray-900 dark:text-white">Prompt Content</h5>
                        <div className="bg-gray-100 dark:bg-gray-600 rounded-lg p-3">
                          <pre className="whitespace-pre-wrap text-sm font-mono text-gray-900 dark:text-white">
                            {selectedTemplate.prompt}
                          </pre>
                        </div>
                        {selectedTemplate.prompt && extractVariables(selectedTemplate.prompt).length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Variables detected: {extractVariables(selectedTemplate.prompt).map(v => (
                                <code key={v} className="bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white px-1 rounded mx-1">
                                  {`{${v}}`}
                                </code>
                              ))}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        <p>Created: {new Date(selectedTemplate.createdAt).toLocaleDateString()}</p>
                        <p>Updated: {new Date(selectedTemplate.updatedAt).toLocaleDateString()}</p>
                      </div>
                      
                      <button
                        onClick={() => startEdit(selectedTemplate)}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Edit Global Template
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <p>Select a template to view details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalTemplateManager;